import { DISCOVERY_PAGES } from '../../domain/discovery';
import type { DiscoveryHypothesisView } from '../../app/discovery';
import type { UndoRowB } from './bandB';
import type { MatchRow } from '../../types';
import { PhaseIcon } from '../PhaseIcon';
import { AddPatternSheet } from '../setup/AddPatternSheet';
import { canAddPattern, SEGMENT_META } from './discoveryB';
import { DiscoveryRowB } from './DiscoveryRowB';
import { HypothesisSheetB } from './HypothesisSheetB';
import { useDiscoveryB } from './useDiscoveryB';

/**
 * B-9 — the Discovery capture surface (design spec §2.4; scenarios 6, 8, 11; critique patch
 * #7). The middle band of the takeover chrome, in two presentations from one component:
 *
 * - **`discovery`** (in-match, `capturePhase 'discovery'`) — the live surface: rows record.
 * - **`preKickoff`** (state `preMatch`, same phase) — the §2.4 preview: identical rendering,
 *   every contact declined by the `canCapture` gate, and a line saying so (never a swallowed
 *   tap). One component for both keeps React state (page, orders) alive across kickoff —
 *   the surface the user arranged is the surface the match starts on.
 *
 * ── Paging is tap-to-switch segments, deliberately not swipe (§2.4) ──────────
 *
 * *"tap-to-switch segments, deliberately **not** swipe-paged (no gesture ambiguity on the hot
 * path)"* — a design ruling this component transcribes, diverging from A's scroll-snap pager on
 * purpose. Four segments (engine `DISCOVERY_PAGES` buckets; all 7 phases map onto them via
 * `discoveryBucketForPhase`), kit phase icons + mono codes, exactly one page rendered at a
 * time. Segment entry re-evaluates DISC-7's gate (`showPage` → `scheduleSettle`) — never
 * bypasses it.
 *
 * ── What is deliberately absent, and whose it is ─────────────────────────────
 *
 * The undo pill (bottom band) is **B-11's** — an increment here already starts the engine's
 * window (`lastIncrementTs` is written by the commit; the pill is a predicate over that
 * committed column). The long-press detail sheet is **B-15's**. The locked board and its
 * "visit Discovery" navigation are **B-10's**.
 */
export interface DiscoverySurfaceBProps {
  /** The shell's fresh row — the single authority on state; re-read after every transition. */
  readonly match: MatchRow;
  /** The shell's already-read rows, seeding the surface's own board once at mount. */
  readonly initialRows: readonly DiscoveryHypothesisView[];
  /** B-11 — the surface publishes its committed row state for the band's undo pill. */
  readonly onRowsChanged?: (rows: readonly UndoRowB[]) => void;
  /** B-11 — bumped after a band undo commits; the surface re-reads and republishes. */
  readonly refreshEpoch?: number;
}

export function DiscoverySurfaceB({
  match,
  initialRows,
  onRowsChanged,
  refreshEpoch,
}: DiscoverySurfaceBProps) {
  const { state, gestures, actions } = useDiscoveryB(match, initialRows, {
    onRowsChanged,
    refreshEpoch,
  });
  // `showPage` only ever receives DISCOVERY_PAGES map indices, so this lookup is total in
  // practice; the guard satisfies `noUncheckedIndexedAccess` without inventing a fallback page.
  const page = state.pages[state.pageIndex];
  if (page === undefined) return null;

  return (
    <div
      className="discb"
      data-testid="discb"
      data-capturing={state.capturing ? 'true' : 'false'}
      data-page={page.bucket}
    >
      {/* §6.4a's negative made visible (A's precedent, kept per baseline §1.14): pre-kickoff
          taps record nothing, and the surface says so rather than swallowing them. The Kickoff
          hold sits in the chrome's top band directly above. */}
      {!state.capturing && (
        <p className="discb-preview" data-testid="discb-not-capturing">
          Not kicked off — taps record nothing yet. Hold the whistle above to start.
        </p>
      )}

      {state.notice !== null && (
        <div className="discb-notice" role="status" data-testid="discb-notice">
          <span>{state.notice}</span>
          <button type="button" onClick={actions.dismissNotice}>
            Dismiss
          </button>
        </div>
      )}

      {/* The 4-segment phase-page control, with the add-pattern affordance beside it — present
          exactly while the engine would accept the add (canAddPattern; critique patch #7). */}
      <div className="discb-bar">
        <div className="discb-segs" role="tablist" aria-label="Game phase pages">
          {DISCOVERY_PAGES.map((bucket, index) => (
            <button
              key={bucket}
              type="button"
              role="tab"
              className="discb-seg"
              aria-selected={index === state.pageIndex}
              aria-label={SEGMENT_META[bucket].label}
              data-testid={`discb-seg-${bucket}`}
              onClick={() => {
                actions.showPage(index);
              }}
            >
              <PhaseIcon phase={bucket} />
              <span className="discb-seg__code">{SEGMENT_META[bucket].code}</span>
            </button>
          ))}
        </div>
        {canAddPattern(match) && (
          <button
            type="button"
            className="discb-add"
            aria-label="Add a pattern"
            data-testid="discb-add"
            onClick={actions.openSheet}
          >
            ＋
          </button>
        )}
      </div>

      {/* Exactly one page at a time; the list is the surface's only scroll region, so the
          segment bar never leaves the thumb's reach. */}
      <section
        className="discb-page"
        data-testid={`discb-page-${page.bucket}`}
        aria-label={SEGMENT_META[page.bucket].label}
      >
        <ul className="discb-rows">
          {page.rows.map((row) => (
            <DiscoveryRowB key={row.id} row={row} gestures={gestures} capturing={state.capturing} />
          ))}
        </ul>
        {page.rows.length === 0 && (
          <p className="discb-empty" data-testid="discb-empty">
            Nothing on this page yet.
          </p>
        )}
      </section>

      {/* B-7's sheet, reused whole (text + the seven-phase picker; SheetB's <dialog> modal).
          Reachable only through the gated control above; the engine's own window stands
          behind it either way (`ERR_NOT_IN_DISCOVERY` / `ERR_MATCH_NOT_CAPTURING`). */}
      {state.sheetOpen && <AddPatternSheet onAdd={actions.addPattern} onClose={actions.closeSheet} />}

      {/* B-15's detail sheet, opened by the 450 ms hold on any row (§2.4). Non-modal over the
          middle band, so the whistle, the flag and the undo pill stay live around it. */}
      {state.detailRow !== null && (
        <HypothesisSheetB
          match={match}
          hypothesis={state.detailRow}
          onChanged={actions.reread}
          onClose={actions.closeDetail}
        />
      )}
    </div>
  );
}
